import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { OAuthProvider } from './context/OAuthContext';
import { ActingAsProvider } from './context/ActingAsContext';
import { TradingStatusProvider } from './context/TradingStatusContext';
import { ToastProvider } from './components/common/ToastContainer';
import Layout from './components/layout/Layout';
import Dashboard from './pages/Dashboard';
import BrokersPage from './pages/BrokersPage';
import BasketsPage from './pages/BasketsPage';
import AllAllocationsPage from './pages/AllAllocationsPage';
import AuthPage from './pages/AuthPage';
import LandingPage from './pages/LandingPage';
import MarketplacePage from './pages/MarketplacePage';
import MarketplaceDetailPage from './pages/MarketplaceDetailPage';
import BuildYourOwnPage from './pages/BuildYourOwnPage';
import UserSubscriptionsPage from './pages/UserSubscriptionsPage';
import AdminMarketplacePage from './pages/AdminMarketplacePage';
import AdminManagedUsersPage from './pages/AdminManagedUsersPage';
import AccountManagerTerminalPage from './pages/AccountManagerTerminalPage';
import SuperAdminGroupsPage from './pages/SuperAdminGroupsPage';
import SuperAdminManagedUsersPage from './pages/SuperAdminManagedUsersPage';
import SuperAdminAuditPage from './pages/SuperAdminAuditPage';
import SuperAdminBasketComparePage from './pages/SuperAdminBasketComparePage';
import SuperAdminPlatformPnlPage from './pages/SuperAdminPlatformPnlPage';
import SuperAdminDataHealthPage from './pages/SuperAdminDataHealthPage';
import MarketplaceAnalyticsListPage from './pages/MarketplaceAnalyticsListPage';
import MarketplaceBackfillPage from './pages/MarketplaceBackfillPage';
import MarketplaceAnalyticsDetailPage from './pages/MarketplaceAnalyticsDetailPage';
import PromotionsAdminPage from './pages/PromotionsAdminPage';
import PromotionLeadsPage from './pages/PromotionLeadsPage';
import LoadingSpinner from './components/common/LoadingSpinner';
import OAuthCallback from './components/oauth/OAuthCallback';
import { isWhiteLabel } from './config/tenantConfig';
import ScrollToTop from './components/common/ScrollToTop';
import OrdersPage from './pages/OrdersPage';
import AnalyticsLayout from './pages/AnalyticsLayout';
import OverviewSection from './pages/analytics/OverviewSection';
import TradesSection from './pages/analytics/TradesSection';
import BasketsSection from './pages/analytics/BasketsSection';
import TimeSection from './pages/analytics/TimeSection';
import ExecutionSection from './pages/analytics/ExecutionSection';
import DownloadSection from './pages/analytics/DownloadSection';
import SchedulePage from './pages/SchedulePage';
import OthersLayout from './pages/OthersLayout';
import TimelineSection from './pages/others/TimelineSection';
import ExpiryDaysSection from './pages/others/ExpiryDaysSection';
import HolidaysSection from './pages/others/HolidaysSection';
import AccountPage from './pages/AccountPage';

// Main App Component (inside AuthProvider)
const AppContent: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();
  const whiteLabel = isWhiteLabel();

  return (
    <BrowserRouter>
      <ScrollToTop />
      <Routes>
        {/* OAuth callback route - accessible without authentication */}
        <Route path="/oauth/callback" element={<OAuthCallback />} />

        {/* Public marketplace routes - only for unauthenticated users */}
        {!isAuthenticated && (
          <>
            <Route path="/marketplace" element={<MarketplacePage />} />
            <Route path="/marketplace/:basketId" element={<MarketplaceDetailPage />} />
            <Route path="/build-your-own" element={<BuildYourOwnPage />} />
          </>
        )}

        {/* Auth page route - redirect to dashboard if already authenticated */}
        <Route
          path="/auth"
          element={
            isLoading && !window.location.pathname.includes('/auth') ? (
              <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950">
                <LoadingSpinner message="Loading application..." />
              </div>
            ) : isAuthenticated ? (
              <Navigate to="/" replace />
            ) : (
              <AuthPage />
            )
          }
        />

        {/* Protected routes with Layout */}
        <Route
          path="/*"
          element={
            isLoading ? (
              <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950">
                <LoadingSpinner message="Loading application..." />
              </div>
            ) : !isAuthenticated ? (
              whiteLabel ? <LandingPage /> : <Navigate to="/auth" replace />
            ) : (
              <Layout />
            )
          }
        >
          <Route index element={<Dashboard />} />
          <Route path="brokers" element={<BrokersPage />} />
          <Route path="baskets" element={<BasketsPage />} />
          {/* Authed users skip the marketing page and go straight to the builder. */}
          <Route path="build-your-own" element={<Navigate to="/baskets" replace />} />
          <Route path="allocations" element={<AllAllocationsPage />} />
          <Route path="orders" element={<OrdersPage />} />
          <Route path="schedule" element={<SchedulePage />} />
          <Route path="marketplace" element={<MarketplacePage />} />
          <Route path="marketplace/:basketId" element={<MarketplaceDetailPage />} />
          <Route path="subscriptions" element={<UserSubscriptionsPage />} />
          <Route path="admin/marketplace" element={<AdminMarketplacePage />} />
          <Route path="admin/managed-users" element={<AdminManagedUsersPage />} />
          <Route path="admin/account-manager/terminal" element={<AccountManagerTerminalPage />} />
          <Route path="admin/super/groups" element={<SuperAdminGroupsPage />} />
          <Route path="admin/super/managed-users" element={<SuperAdminManagedUsersPage />} />
          <Route path="admin/super/audit" element={<SuperAdminAuditPage />} />
          <Route path="admin/super/compare-baskets" element={<SuperAdminBasketComparePage />} />
          <Route path="admin/super/platform-pnl" element={<SuperAdminPlatformPnlPage />} />
          <Route path="admin/super/data-health" element={<SuperAdminDataHealthPage />} />
          <Route path="admin/marketplace-analytics" element={<MarketplaceAnalyticsListPage />} />
          <Route path="admin/marketplace-analytics/:basketId" element={<MarketplaceAnalyticsDetailPage />} />
          <Route path="admin/marketplace-backfill" element={<MarketplaceBackfillPage />} />
          <Route path="admin/promotions" element={<PromotionsAdminPage />} />
          <Route path="admin/promotions/:promoId/leads" element={<PromotionLeadsPage />} />
          {/* Legacy direct URL — keep working but redirect into Others. */}
          <Route path="timeline" element={<Navigate to="/others/timeline" replace />} />
          <Route path="others" element={<OthersLayout />}>
            <Route index             element={<Navigate to="/others/expiry-days" replace />} />
            <Route path="expiry-days" element={<ExpiryDaysSection />} />
            <Route path="holidays"   element={<HolidaysSection />} />
            <Route path="timeline"   element={<TimelineSection />} />
            {/* Analytics nested under Others so its left rail renders inside
                Others' tab strip — single navigation context, no orphan view. */}
            <Route path="analytics" element={<AnalyticsLayout />}>
              <Route index            element={<OverviewSection />} />
              <Route path="baskets"   element={<BasketsSection />} />
              <Route path="trades"    element={<TradesSection />} />
              <Route path="time"      element={<TimeSection />} />
              <Route path="execution" element={<ExecutionSection />} />
              <Route path="download"  element={<DownloadSection />} />
            </Route>
          </Route>
          <Route path="settings" element={<div className="space-y-6"><h1 className="text-2xl font-bold">Settings</h1><p>Settings configuration will be available here.</p></div>} />
          <Route path="account" element={<AccountPage />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
};

// Root App Component
const App: React.FC = () => {
  return (
    <AuthProvider>
      <ActingAsProvider>
        <OAuthProvider>
          <TradingStatusProvider>
            <ToastProvider position="top-right">
              <AppContent />
            </ToastProvider>
          </TradingStatusProvider>
        </OAuthProvider>
      </ActingAsProvider>
    </AuthProvider>
  );
};

export default App;
